import { Avatar, Button, Input, Select, Table, TableProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Suspense, lazy, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { searchPeoples } from '@/api';

import { CandidateListSkeleton } from '@/components/skeleton.effect';

import { ApiResponse, Expert } from '@/interface';

import { assignExpertsRequest } from '@/redux/slices/expert.slice';
import { AppDispatch, RootState } from '@/redux/store';

const ExpertExpandedContent = lazy(() => import('@/components/expertExpandedContent'));

const STATUS_FLOW = [
  'Suggested',
  'Submitted',
  'Approved',
  'Waiting for Call',
  'Waiting for Meeting',
  'Waiting for Invoice',
  'Invoiced',
];

interface FilterDataType {
  title: string[];
  experience: string;
  skills: string;
  location: string;
  company: string;
  limit: string;
}

interface SearchedPerson {
  id: string;
  photoUrl?: string;
  firstName: string;
  lastName: string;
  title?: string;
  company?: string;
}

interface SuggestedExpertRow extends SearchedPerson {
  status: string;
}

interface SearchPeopleResponse {
  people: SearchedPerson[];
}

export const SuggestedExperts = () => {
  const [searching, setSearching] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [selectedRowsData, setSelectedRowsData] = useState<SuggestedExpertRow[]>([]);

  const navigate = useNavigate();
  const [addingExperts, setAddingExperts] = useState(false);
  const [dataSource, setDataSource] = useState<SuggestedExpertRow[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const { projectDetail, loading } = useSelector((state: RootState) => state.project);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filterData, setFilterData] = useState<FilterDataType>({
    title: [],
    experience: '',
    skills: '',
    location: '',
    company: '',
    limit: '20',
  });
  const parseTitles = (raw: string[] | string | null | undefined): string[] => {
    if (!raw) if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((t) => t.trim());

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          return (parsed as string[]).map((t) => t.trim());
        }
      } catch {
        return raw
          .replace(/[{}"]/g, '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
      }
    }

    return [];
  };
  useEffect(() => {
    setFilterData({
      title: parseTitles(projectDetail?.filter?.title),
      experience: projectDetail?.filter?.experience?.toString() || '',
      skills: projectDetail?.filter?.skills?.join(', ') || '',
      location: projectDetail?.filter?.location?.join(', ') || '',
      company: projectDetail?.filter?.company?.join(', ') || '',
      limit: projectDetail?.filter?.limit?.toString() || '',
    });
  }, [projectDetail]);

  const handleStatusChange = (newStatus: string, recordId: string) => {
    setDataSource((prevDataSource) =>
      prevDataSource.map((record) => (record.id === recordId ? { ...record, status: newStatus } : record)),
    );
    toast.success(`Status updated to '${newStatus}' for ${recordId}.`);
  };

  const columns: ColumnsType<SuggestedExpertRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, record: SuggestedExpertRow) => (
        <div className="flex items-center gap-2">
          <Avatar
            src={record?.photoUrl}
            size={40}
            alt={`${record?.firstName} ${record?.lastName}`}
            className="bg-primary/20 text-black"
          >
            {record?.firstName?.charAt(0).toUpperCase()}
          </Avatar>
          <span>{`${record.firstName} ${record.lastName}`}</span>
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (text ? <div>{text}</div> : '-'),
    },
    {
      title: 'Organization',
      dataIndex: 'company',
      key: 'organization',
      render: (text: string) => (text ? <div>{text}</div> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      key: 'status',
      render: (status: string, record: SuggestedExpertRow) => {
        const currentStatusIndex = STATUS_FLOW.indexOf(status);
        const options =
          currentStatusIndex !== -1 ? STATUS_FLOW.slice(currentStatusIndex + 1).map((s) => ({ label: s, value: s })) : [];

        const isDisabled = status === 'Approved';

        return (
          <Select
            value={status || 'N/A'}
            style={{ width: 180 }}
            onChange={(value) => handleStatusChange(value, record.id)}
            disabled={isDisabled}
          >
            <Select.Option value={status} disabled>
              {status}
            </Select.Option>
            {options.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
  ];
  const rowSelection: TableProps<SuggestedExpertRow>['rowSelection'] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: SuggestedExpertRow[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRowsData(selectedRows);
      console.log('Selected Rows:', selectedRows);
    },
  };

  const handleSearch = async () => {
    if (searching) return;
    setSearching(true);
    try {
      const searchFilter = {
        ...filterData,
        experience: { gte: parseInt(filterData.experience) || 0 },
        skills: filterData.skills.split(',').map((skill) => skill.trim()),
        limit: parseInt(filterData.limit) || 3,
        projectId: projectDetail?.id || '',
      };
      const response = (await searchPeoples(searchFilter)) as ApiResponse<SearchPeopleResponse>;
      if (response.data.people.length > 0) {
        const peopleWithStatus: SuggestedExpertRow[] = response.data.people.map((person) => ({
          ...person,
          id: person.id,
          status: 'Suggested',
        }));
        setDataSource(peopleWithStatus);
        toast.success('Candidates fetched successfully!');
      } else {
        toast.error('No candidates found.');
      }
    } catch (error) {
      console.error('Error searching candidates:', error);
      toast.error('Failed to fetch candidates.');
    } finally {
      setSearching(false);
    }
  };
  const handleAddToExperts = async () => {
    if (selectedRowKeys.length === 0) {
      toast.error('Please select at least one candidate.');
      return;
    }

    console.log('Selected Records:', selectedRowsData);

    setAddingExperts(true);

    try {
      await dispatch(
        assignExpertsRequest({
          projectId: projectDetail!.id,
          expertIds: selectedRowKeys as string[],
        }),
      ).unwrap();

      toast.success(`${selectedRowKeys.length} expert(s) added successfully.`);
      navigate(`/project-detail/${projectDetail?.id}/overview`);
    } catch (error) {
      console.error('Failed to add experts:', error);
      toast.error('Failed to add experts.');
    } finally {
      setAddingExperts(false);
    }
  };

  return (
    <div className="h-screen overflow-y-auto p-4 bg-white rounded-md shadow">
      <Toaster />

      <div className="relative">
        <div className="text-base font-semibold">Modify Filters</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(filterData).map((key) => (
            <div key={key}>
              <label className="block text-sm font-medium capitalize">{key}</label>
              <Input
                value={filterData[key as keyof FilterDataType]}
                onChange={(e) => setFilterData({ ...filterData, [key]: e.target.value })}
                disabled={loading}
              />
              {key === 'skills' && <p className="text-xs text-gray-500">Enter skills separated by commas (e.g., React, Node)</p>}
            </div>
          ))}
        </div>

        {searching && (
          <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-80 z-10">
            <span className="text-gray-600">Searching...</span>
          </div>
        )}

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button onClick={handleSearch} className="bg-primary text-white" disabled={searching}>
            Search
          </Button>

          <Button
            onClick={handleAddToExperts}
            disabled={selectedRowKeys.length === 0 || addingExperts}
            className={`${
              selectedRowKeys.length === 0 || addingExperts
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {addingExperts ? 'Adding...' : 'Add to Experts'}
          </Button>
        </div>
      </div>

      {searching && <CandidateListSkeleton />}

      <Table
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        scroll={{ x: 300 }}
        className="mt-6"
        dataSource={dataSource}
        columns={columns}
        rowKey="id"
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Suspense fallback={<div className="p-4 text-gray-500">Loading details...</div>}>
                {/* searchPeoples only returns a lightweight subset of Expert's fields; ExpertExpandedContent
                    renders the rest defensively (optional chaining) so this cast reflects a pre-existing
                    shape mismatch, not a real Expert object. */}
                <ExpertExpandedContent record={record as unknown as Expert} />
              </Suspense>
            );
          },
          rowExpandable: () => true,
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          onExpand: (expanded, record) => {
            setExpandedRowKey(expanded ? record.id : null);
          },
        }}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};
